import { useState, useMemo, useCallback } from 'react';
import { Button, Form, FormInstance, Input, Tag } from 'antd';
import { EditOutlined, CloseOutlined } from '@ant-design/icons';
import { CrudSchemaFields, type EnsoFormItem } from '@ohpen/enso-ui';
import { useTranslation } from 'react-i18next';
import { FirmFcaData } from '@pmp/api';
import { formatAddressAsLines, getHighlightProps } from '../utils';
import styles from '../styles.module.css';

interface AddressFieldProps {
  readonly: boolean;
  form?: FormInstance;
  getAddress?: () => any;
  fcaModifiedFields?: FirmFcaData | null;
  removeFcaModifiedField?: (key: keyof FirmFcaData, nestedKey?: string) => void;
  isFromFca?: boolean;
  namePrefix?: (string | number)[];
  titleKey?: string;
  fcaFieldKey?: keyof FirmFcaData;
  fcaFieldNestedKey?: string;
}

export const AddressField: React.FC<AddressFieldProps> = ({
  readonly,
  form,
  getAddress,
  fcaModifiedFields,
  removeFcaModifiedField,
  isFromFca,
  namePrefix,
  titleKey = 'lender.firms.labels.tradingOfficeAddress',
  fcaFieldKey = 'tradingNames',
  fcaFieldNestedKey,
}) => {
  const prefixPath = useCallback(
    (path: (string | number)[]) => (namePrefix ? [...namePrefix, ...path] : path),
    [namePrefix],
  );
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const type = Form.useWatch(['registrationDetails', 'type'], form);

  const isFcaRegistered = useMemo(() => type !== 'NOT_FCA_REGISTERED', [type]);

  const activeFcaFields = useMemo(() => (isFromFca ? fcaModifiedFields : null), [isFromFca, fcaModifiedFields]);
  const clearFca = useCallback(
    () => removeFcaModifiedField?.(fcaFieldKey, fcaFieldNestedKey),
    [removeFcaModifiedField, fcaFieldKey, fcaFieldNestedKey],
  );
  const fcaHighlightPath = useMemo(
    () => (fcaFieldNestedKey ? [fcaFieldKey as string, fcaFieldNestedKey] : [fcaFieldKey as string]),
    [fcaFieldKey, fcaFieldNestedKey],
  );

  const addressTitle = useMemo(
    () => (
      <div className={styles.sectionTitleRow}>
        <span className={styles.sectionTitleLabel}>
          {t(titleKey)}
          {isFcaRegistered && isFromFca && (
            <Tag variant="outlined" color="blue" className={styles.fcaTag}>
              {t('lender.firms.labels.fca')}
            </Tag>
          )}
        </span>
        <div>
          {!readonly && (
            <Button type="link" icon={isEditing ? <CloseOutlined /> : <EditOutlined />} onClick={() => setIsEditing(!isEditing)} />
          )}
        </div>
      </div>
    ),
    [readonly, t, isEditing, isFcaRegistered, isFromFca, titleKey],
  );

  const detailColumns: EnsoFormItem[] = useMemo(
    () => [
      {
        valueType: 'group' as const,
        title: addressTitle,
        columns: [],
        colProps: { span: 24 },
      },
      {
        valueType: 'group' as const,
        rowProps: { style: { marginBottom: 16 } },
        columns: [
          {
            title: t('lender.firms.labels.addressLine1'),
            dataIndex: prefixPath(['lineItems', 0]),
            valueType: 'text' as const,
            readonly,
            fieldProps: () => ({
              placeholder: t('lender.firms.labels.addressLine1Placeholder'),
              className: getHighlightProps(fcaHighlightPath, activeFcaFields),
              onBlur: clearFca,
            }),
            formItemProps: { rules: [{ required: isEditing }] },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.addressLine2'),
            dataIndex: prefixPath(['lineItems', 1]),
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              placeholder: t('lender.firms.labels.addressLine2Placeholder'),
              className: getHighlightProps(fcaHighlightPath, activeFcaFields),
              onBlur: clearFca,
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group' as const,
        rowProps: { style: { marginBottom: 16 } },
        columns: [
          {
            title: t('lender.firms.labels.addressLine3'),
            dataIndex: prefixPath(['lineItems', 2]),
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              className: getHighlightProps(fcaHighlightPath, activeFcaFields),
              onBlur: clearFca,
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.addressLine4'),
            dataIndex: prefixPath(['lineItems', 3]),
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              className: getHighlightProps(fcaHighlightPath, activeFcaFields),
              onBlur: clearFca,
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group' as const,
        rowProps: { style: { marginBottom: 16 } },
        columns: [
          {
            title: t('lender.firms.labels.town'),
            dataIndex: prefixPath(['city']),
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              className: getHighlightProps(fcaHighlightPath, activeFcaFields),
              onBlur: clearFca,
            },
            formItemProps: { rules: [{ required: isEditing }] },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.county'),
            dataIndex: prefixPath(['lineItems', 4]),
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              className: getHighlightProps(fcaHighlightPath, activeFcaFields),
              onBlur: clearFca,
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group' as const,
        rowProps: { style: { marginBottom: 16 } },
        columns: [
          {
            title: t('lender.firms.labels.postCode'),
            dataIndex: prefixPath(['postCode']),
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              className: getHighlightProps(fcaHighlightPath, activeFcaFields),
              onBlur: clearFca,
            },
            formItemProps: { rules: [{ required: isEditing }] },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.country'),
            dataIndex: prefixPath(['country']),
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              className: getHighlightProps(fcaHighlightPath, activeFcaFields),
              onBlur: clearFca,
            },
            colProps: { span: 12 },
          },
        ],
      },
    ],
    [t, readonly, addressTitle, activeFcaFields, prefixPath, fcaHighlightPath, clearFca, isEditing],
  );

  const previewColumns: EnsoFormItem[] = useMemo(
    () => [
      {
        valueType: 'group' as const,
        columns: [
          {
            title: addressTitle,
            renderFormItem: () => (
              <Input.TextArea
                value={formatAddressAsLines(getAddress?.() ?? {})}
                autoSize={{ minRows: 2 }}
                disabled
                className={getHighlightProps(fcaHighlightPath, activeFcaFields)}
              />
            ),
            formItemProps: { colon: false },
            colProps: { span: 12 },
          },
        ],
      },
    ],
    [getAddress, addressTitle, activeFcaFields, fcaHighlightPath],
  );

  return (
    <CrudSchemaFields
      schema={isEditing ? detailColumns : previewColumns}
      form={form}
      readonly={readonly}
      component={false}
    />
  );
};
